import { Link } from 'react-router-dom';
import PublicShell from '@/components/layout/PublicShell';
import useSeo from '@/hooks/useSeo';

const documents = {
    privacidad: {
        title: 'Política de Privacidad',
        updated: 'Última actualización: agosto de 2026',
        sections: [
            {
                heading: 'Qué datos recopilamos',
                body: [
                    'Recopilamos la información que nos proporcionás al registrarte y usar la plataforma: nombre, DNI, correo electrónico, teléfono y, en el caso de alumnos, medidas corporales registradas por tu gimnasio.',
                    'También registramos datos de uso de la plataforma, como fechas de acceso, interacciones y preferencias de configuración.',
                ],
            },
            {
                heading: 'Para qué los utilizamos',
                body: [
                    'Utilizamos tus datos para operar la plataforma, gestionar membresías y clases, procesar pagos, enviar notificaciones relacionadas con el servicio y mejorar la experiencia de uso.',
                ],
            },
            {
                heading: 'Cómo los almacenamos y protegemos',
                body: [
                    'Los datos se almacenan en servidores seguros con acceso restringido. Aplicamos medidas técnicas y organizativas razonables para proteger tu información contra accesos no autorizados, pérdida o alteración.',
                ],
            },
            {
                heading: 'Datos de alumnos',
                body: [
                    'Para los alumnos registrados en un gimnasio, se almacenan datos como nombre, DNI, email, teléfono y medidas corporales. Estos datos son gestionados por el gimnasio responsable de tu cuenta y se utilizan para el seguimiento de tu actividad.',
                ],
            },
            {
                heading: 'Cookies y tecnologías de seguimiento',
                body: [
                    'Utilizamos cookies y tecnologías similares para mantener tu sesión, recordar preferencias y analizar el uso del sitio. Podés gestionar o deshabilitar las cookies desde la configuración de tu navegador.',
                ],
            },
            {
                heading: 'Servicios de terceros',
                body: [
                    'Podemos compartir información con proveedores de servicios que nos ayudan a operar la plataforma (procesamiento de pagos, analítica, hosting). Estos terceros acceden únicamente a la información necesaria para prestar su servicio y están obligados a protegerla.',
                ],
            },
            {
                heading: 'Derechos del usuario sobre sus datos',
                body: [
                    'Tenés derecho a acceder, rectificar, actualizar o solicitar la eliminación de tus datos personales. Para ejercer estos derechos, contactanos a través del correo indicado al final de esta política.',
                ],
            },
            {
                heading: 'Contacto para cuestiones de privacidad',
                body: [
                    'Para cualquier consulta sobre esta política o sobre el tratamiento de tus datos, escribinos a hola@gyms.ar.',
                ],
            },
        ],
    },
    terminos: {
        title: 'Términos y Condiciones de Uso',
        updated: 'Última actualización: agosto de 2026',
        sections: [
            {
                heading: 'Reglas para utilizar gyms.ar',
                body: [
                    'Al acceder o utilizar GYMS.ar aceptás estos Términos y Condiciones. Si no estás de acuerdo, no utilices la plataforma.',
                ],
            },
            {
                heading: 'Registro y cuentas',
                body: [
                    'Para acceder a ciertas funcionalidades debés crear una cuenta con información veraz y mantenerla actualizada. Sos responsable de la confidencialidad de tus credenciales y de toda actividad realizada con tu cuenta.',
                ],
            },
            {
                heading: 'Responsabilidades del gimnasio',
                body: [
                    'El gimnasio es responsable de gestionar a sus alumnos, membresías, clases y pagos, así como de la veracidad de la información que carga en la plataforma y del cumplimiento de la normativa aplicable a su actividad.',
                ],
            },
            {
                heading: 'Responsabilidades del alumno',
                body: [
                    'El alumno es responsable de asistir a las clases inscriptas, mantener su información actualizada y hacer un uso adecuado de la plataforma y de los servicios del gimnasio.',
                ],
            },
            {
                heading: 'Uso permitido de la plataforma',
                body: [
                    'La plataforma debe utilizarse únicamente para los fines previstos. Queda prohibido el uso indebido, fraudulento o que infrinja derechos de terceros o la legislación vigente.',
                ],
            },
            {
                heading: 'Suspensión o cancelación de cuentas',
                body: [
                    'Podemos suspender o cancelar cuentas que incumplan estos términos o hagan un uso indebido de la plataforma. También podés solicitar la baja de tu cuenta en cualquier momento.',
                ],
            },
            {
                heading: 'Propiedad intelectual',
                body: [
                    'La plataforma, su diseño, marcas y contenidos son propiedad de sus titulares. No se otorga ninguna licencia sobre ellos más allá del uso previsto de la plataforma.',
                ],
            },
            {
                heading: 'Limitación de responsabilidad',
                body: [
                    'La plataforma se brinda "tal cual" y, en la medida permitida por la ley, no garantizamos su disponibilidad ininterrumpida ni la ausencia de errores. No somos responsables por daños indirectos o consecuentes derivados del uso del servicio.',
                ],
            },
            {
                heading: 'Modificaciones del servicio',
                body: [
                    'Podemos modificar o discontinuar funcionalidades de la plataforma y actualizar estos términos. Los cambios se comunicarán a través de la plataforma.',
                ],
            },
            {
                heading: 'Legislación aplicable',
                body: [
                    'Estos términos se rigen por la legislación de la República Argentina. Cualquier controversia será sometida a los tribunales competentes de la jurisdicción correspondiente.',
                ],
            },
        ],
    },
    pagos: {
        title: 'Política de Pagos y Reembolsos',
        updated: 'Última actualización: agosto de 2026',
        sections: [
            {
                heading: 'Métodos de pago',
                body: [
                    'Aceptamos los medios de pago habilitados por la plataforma, que pueden incluir tarjetas de crédito y débito, transferencias y efectivo según lo configure cada gimnasio.',
                ],
            },
            {
                heading: 'Cuándo se considera realizado un pago',
                body: [
                    'Un pago se considera realizado cuando es confirmado por el procesador de pagos correspondiente y acreditado en la cuenta del gimnasio.',
                ],
            },
            {
                heading: 'Renovaciones',
                body: [
                    'Las membresías y planes con renovación automática se renuevan según el período contratado. Podés gestionar las renovaciones desde tu cuenta.',
                ],
            },
            {
                heading: 'Cancelaciones',
                body: [
                    'Podés cancelar tu plan o membresía según los términos informados al momento de la contratación. La cancelación no genera reembolsos automáticos por períodos ya facturados.',
                ],
            },
            {
                heading: 'Reembolsos',
                body: [
                    'Los reembolsos se evalúan caso por caso. Para solicitarlos, contactá al gimnasio responsable de tu cuenta o escribinos a hola@gyms.ar.',
                ],
            },
            {
                heading: 'Pagos rechazados',
                body: [
                    'Si un pago es rechazado, la operación no se concreta y deberás reintentarlo con un medio de pago válido. Los servicios vinculados podrán suspenderse hasta regularizar el pago.',
                ],
            },
            {
                heading: 'Membresías vencidas',
                body: [
                    'Las membresías vencidas pueden limitar el acceso a las clases y servicios del gimnasio hasta su renovación.',
                ],
            },
            {
                heading: 'Interrupción del servicio',
                body: [
                    'Ante una interrupción prolongada del servicio no imputable al usuario, evaluaremos el otorgamiento de compensaciones según corresponda.',
                ],
            },
        ],
    },
    cookies: {
        title: 'Política de Cookies',
        updated: 'Última actualización: agosto de 2026',
        sections: [
            {
                heading: 'Cookies necesarias',
                body: [
                    'Son esenciales para el funcionamiento de la plataforma, como mantener tu sesión iniciada y permitir la navegación segura. No pueden deshabilitarse.',
                ],
            },
            {
                heading: 'Cookies de sesión',
                body: [
                    'Se utilizan para recordar tu actividad durante una sesión y facilitar el uso del servicio mientras navegás.',
                ],
            },
            {
                heading: 'Analítica',
                body: [
                    'Utilizamos cookies de analítica para entender cómo se usa la plataforma y mejorar su funcionamiento. La información se recopila de forma agregada.',
                ],
            },
            {
                heading: 'Preferencias',
                body: [
                    'Guardan tus elecciones y preferencias para personalizar tu experiencia, como el idioma o la configuración de notificaciones.',
                ],
            },
            {
                heading: 'Tecnologías de terceros',
                body: [
                    'Algunos servicios de terceros que integramos (como procesadores de pago o analítica) pueden instalar sus propias cookies, sujetas a sus respectivas políticas.',
                ],
            },
            {
                heading: 'Cómo deshabilitarlas',
                body: [
                    'Podés configurar tu navegador para bloquear o eliminar cookies. Tené en cuenta que algunas funcionalidades de la plataforma pueden verse afectadas.',
                ],
            },
        ],
    },
};

export default function LegalPage({ slug }) {
    const doc = documents[slug];

    useSeo(doc?.title, doc ? `${doc.title} de GYMS.ar. Conocé los términos, derechos y condiciones de uso de la plataforma.` : undefined);

    if (!doc) {
        return null;
    }

    return (
        <PublicShell>
            <section className="pt-40 pb-24 px-6 lg:px-10 bg-black">
                <div className="max-w-3xl mx-auto">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="w-px h-5 bg-white/60" />
                        <span className="micro-cap text-white/60 tracking-[0.2em]">Legal</span>
                    </div>
                    <h1 className="text-white mb-3">{doc.title}</h1>
                    <p className="caption text-white/50 mb-12">{doc.updated}</p>

                    <div className="space-y-10">
                        {doc.sections.map((section, i) => (
                            <div key={i}>
                                <h2 className="text-white mb-3" style={{ fontSize: '1.25rem', letterSpacing: '0.04em' }}>
                                    {section.heading}
                                </h2>
                                {section.body.map((paragraph, j) => (
                                    <p key={j} className="text-white/70 leading-relaxed mb-3 text-base">
                                        {paragraph}
                                    </p>
                                ))}
                            </div>
                        ))}
                    </div>

                    <div className="mt-16 pt-8 border-t border-hairline-dark">
                        <p className="text-white/60 text-base">
                            ¿Tenés dudas? Escribinos a{' '}
                            <a href="mailto:hola@gyms.ar" className="underline hover:text-white transition-colors">
                                hola@gyms.ar
                            </a>
                            .
                        </p>
                        <Link
                            to="/"
                            className="micro-cap text-white/60 hover:text-white transition-colors inline-block mt-4"
                        >
                            Volver al inicio
                        </Link>
                    </div>
                </div>
            </section>
        </PublicShell>
    );
}
